import { Card, CardContent, CardHeader, Typography } from "@mui/material";
import { Canvas } from "@react-three/fiber";
import { useEffect, useMemo, useState } from "react";
import * as THREE from "three";

const RenderSki = ({
  groupRotationInit = { x: 0, y: 0, z: (-1.0 * Math.PI) / 2 },
  cameraPositionInit = { x: 0, y: 0, z: 2200 },
  groupPositionInit = { x: -170, y: 1020, z: 0.0 },
  height,
  width,
  showKeyboard = true,
  dxfSki,
}: {
  groupRotationInit?: { x: number; y: number; z: number };
  cameraPositionInit?: { x: number; y: number; z: number };
  groupPositionInit?: { x: number; y: number; z: number };
  height?: number;
  width?: number;
  showKeyboard?: boolean;
  dxfSki: any;
}) => {
  const [groupRotation, setGroupRotation] = useState(
    new THREE.Euler(groupRotationInit.x, groupRotationInit.y, groupRotationInit.z)
  );
  const [cameraPosition] = useState(
    new THREE.Vector3(cameraPositionInit.x, cameraPositionInit.y, cameraPositionInit.z)
  );
  const [groupPosition, setGroupPosition] = useState(
    new THREE.Vector3(groupPositionInit.x, groupPositionInit.y, groupPositionInit.z)
  );

  const moveIncrement = 20;

  useEffect(() => {
    const keyDown = (e: KeyboardEvent) => {
      if (e.key === "w") {
        setGroupPosition((prev) =>
          new THREE.Vector3(prev.x, prev.y + moveIncrement, prev.z)
        );
      } else if (e.key === "s") {
        setGroupPosition((prev) =>
          new THREE.Vector3(prev.x, prev.y - moveIncrement, prev.z)
        );
      } else if (e.key === "d") {
        setGroupPosition((prev) =>
          new THREE.Vector3(prev.x + moveIncrement, prev.y, prev.z)
        );
      } else if (e.key === "a") {
        setGroupPosition((prev) =>
          new THREE.Vector3(prev.x - moveIncrement, prev.y, prev.z)
        );
      } else if (e.key === "[") {
        setGroupPosition((prev) =>
          new THREE.Vector3(prev.x, prev.y, prev.z - moveIncrement)
        );
      } else if (e.key === "]") {
        setGroupPosition((prev) =>
          new THREE.Vector3(prev.x, prev.y, prev.z + moveIncrement)
        );
      } else if (e.key === "z") {
        setGroupRotation((prev) =>
          new THREE.Euler(prev.x, prev.y, prev.z + (moveIncrement / 1800) * Math.PI)
        );
      } else if (e.key === "x") {
        setGroupRotation((prev) =>
          new THREE.Euler(prev.x, prev.y, prev.z - (moveIncrement / 1800) * Math.PI)
        );
      } else if (e.key === "c") {
        setGroupRotation((prev) =>
          new THREE.Euler(prev.x, prev.y + (moveIncrement / 1800) * Math.PI, prev.z)
        );
      } else if (e.key === "v") {
        setGroupRotation((prev) =>
          new THREE.Euler(prev.x, prev.y - (moveIncrement / 1800) * Math.PI, prev.z)
        );
      }
    };

    window.addEventListener("keydown", keyDown);
    return () => window.removeEventListener("keydown", keyDown);
  }, []);

  const sidecutCorrection = 1.5;
  //const camera = new THREE.PerspectiveCamera(90, width / height, 0.1, 100000)

  const [sidecut1_1Geometry, sidecut1_2Geometry] = useMemo(() => {
    const sidecutArcs1 = dxfSki?.arcs1.filter((arc: any) => arc.d === -1);
    const sidecut1_1 = new THREE.Shape();
    for (let i = 0; i < sidecutArcs1?.slice(0, 1).length; i++) {
      const arc = sidecutArcs1.slice(0, 1)[i];
      sidecut1_1.absarc(
        -1.0 * arc.x,
        arc.y,
        arc.r,
        ((arc.s - sidecutCorrection) / 180) * Math.PI,
        (arc.e / 180) * Math.PI,
        false
      );
    }

    const sidecut1_2 = new THREE.Shape();
    for (let i = 0; i < sidecutArcs1?.slice(1).length; i++) {
      const arc = sidecutArcs1.slice(1)[i];
      sidecut1_2.absarc(
        -1.0 * arc.x,
        arc.y,
        arc.r,
        (arc.s / 180) * Math.PI,
        ((arc.e + sidecutCorrection) / 180) * Math.PI,
        false
      );
    }

    return [new THREE.ShapeGeometry(sidecut1_1), new THREE.ShapeGeometry(sidecut1_2)];
  }, [dxfSki]);

  const [sidecut2_1Geometry, sidecut2_2Geometry] = useMemo(() => {
    const sidecutArcs2 = dxfSki?.arcs2.filter((arc: any) => arc.d === -1);
    const sidecut2_1 = new THREE.Shape();
    for (let i = 0; i < sidecutArcs2?.slice(0, 1).length; i++) {
      const arc = sidecutArcs2.slice(0, 1)[i];
      sidecut2_1.absarc(
        -1.0 * arc.x,
        arc.y,
        arc.r,
        ((arc.s - sidecutCorrection) / 180) * Math.PI,
        (arc.e / 180) * Math.PI,
        false
      );
    }

    const sidecut2_2 = new THREE.Shape();
    for (let i = 0; i < sidecutArcs2?.slice(1).length; i++) {
      const arc = sidecutArcs2.slice(1)[i];
      sidecut2_2.absarc(
        -1.0 * arc.x,
        arc.y,
        arc.r,
        (arc.s / 180) * Math.PI,
        ((arc.e + sidecutCorrection) / 180) * Math.PI,
        false
      );
    }

    return [new THREE.ShapeGeometry(sidecut2_1), new THREE.ShapeGeometry(sidecut2_2)];
  }, [dxfSki]);

  const ski1Geometry = useMemo(() => {
    const ski1 = new THREE.Shape();

    const shapeArcs1 = dxfSki?.arcs1.filter((arc: any) => arc.d === 1);

    for (let i = 0; i < shapeArcs1?.length; i++) {
      const arc = shapeArcs1[i];
      ski1.absarc(arc.x, arc.y, arc.r, (arc.s / 180) * Math.PI, (arc.e / 180) * Math.PI, false);
    }
    return new THREE.ShapeGeometry(ski1);
  }, [dxfSki]);

  const ski2Geometry = useMemo(() => {
    const ski2 = new THREE.Shape();

    const shapeArcs2 = dxfSki?.arcs2.filter((arc: any) => arc.d === 1);

    for (let i = 0; i < shapeArcs2?.length; i++) {
      const arc = shapeArcs2[i];
      ski2.absarc(arc.x, arc.y, arc.r, (arc.s / 180) * Math.PI, (arc.e / 180) * Math.PI, false);
    }
    return new THREE.ShapeGeometry(ski2);
  }, [dxfSki]);

  return (
    <Card elevation={4}>
      <CardHeader title="Render" />
      <CardContent>
        <Canvas
          linear
          flat
          className="skiCanvas"
          camera={{
            fov: 50,
            near: 0.1,
            far: 100000,
            position: cameraPosition,
          }}
          gl={{ preserveDrawingBuffer: true }}
          style={{ height: height, width: width }}
        >
          <scene>
            <group position={groupPosition} rotation={groupRotation}>
              <mesh geometry={ski1Geometry}>
                <meshBasicMaterial color="red" />
              </mesh>
              <mesh visible geometry={ski2Geometry}>
                <meshBasicMaterial color="red" />
              </mesh>
              <mesh visible geometry={sidecut1_1Geometry}>
                <meshBasicMaterial color="white" />
              </mesh>
              <mesh visible geometry={sidecut1_2Geometry}>
                <meshBasicMaterial color="white" />
              </mesh>
              <mesh visible geometry={sidecut2_1Geometry}>
                <meshBasicMaterial color="white" />
              </mesh>
              <mesh visible geometry={sidecut2_2Geometry}>
                <meshBasicMaterial color="white" />
              </mesh>
            </group>
          </scene>
        </Canvas>
        {/* <Typography>Camera Position: {JSON.stringify(cameraPosition)}</Typography>
          <Typography>Group Position: {JSON.stringify(groupPosition)}</Typography>
          <Typography>Group Rotation: {JSON.stringify(groupRotation)}</Typography> */}
        {showKeyboard && (
          <>
            <Typography>Position Skis</Typography>
            <Typography>Move: W,S,A,D</Typography>
            <Typography>Zoom: [,]</Typography>
            <Typography>Rotate: Z,X,C,V</Typography>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default RenderSki;
